import React, { FC, useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from 'react-hot-toast'
import { motion } from 'framer-motion'
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  getDoc,
} from 'firebase/firestore'
import { firebaseDb } from '@/firebase/init'
import { useSelector } from 'react-redux'
import authSelector from '@/redux/selectors/auth'
import { InteractivePaper } from '@/types/interactive-paper'
import { IUnifiedExamSpherePaper } from '@/types/exam-sphere'
import { convertInteractivePaperToUnified } from '@/utils/exam-sphere'
import { ExamSpherePaperRow } from '@/components/ui/common/exam-sphere'
import { CommonBreadCrumbs } from '@/components/ui'
import { DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { ExamSphereAnimatedBackground } from '@/components/page-partials/exam-sphere/components/ExamSphereAnimatedBackground'
import { Cube3dSphereIcon } from '@/components/icons/cube-3d-sphere-icon'
import { trackEventMixpanel } from '@/utils/mixpanel'
import { INTERACTIVE_PAPER_DELETED } from '@/utils/mixpanel/eventNames'

interface Props {
  anonymousUserId: string
}

const headerVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut' as const,
    },
  },
}

const emptyStateVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: 'easeOut' as const,
    },
  },
}

const InteractivePapersList: FC<Props> = ({ anonymousUserId }) => {
  const router = useRouter()
  const [papers, setPapers] = useState<InteractivePaper[]>([])
  const [unifiedPapers, setUnifiedPapers] = useState<IUnifiedExamSpherePaper[]>(
    [],
  )
  const [loading, setLoading] = useState<boolean>(true)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)
  const [isInIframe, setIsInIframe] = useState(false)

  // Get auth state to check if user is logged in
  const { user } = useSelector(authSelector)

  // Detect if running inside an iframe
  useEffect(() => {
    try {
      // Only access window if we're on the client side
      if (typeof window !== 'undefined') {
        setIsInIframe(window !== window.parent)
      }
    } catch (e) {
      // If we can't access window.parent due to cross-origin restrictions,
      // assume we're in an iframe for safety
      setIsInIframe(true)
    }
  }, [])

  // Load papers when user ID is available
  useEffect(() => {
    // At least one of these should be available to load papers
    if (!anonymousUserId && !user) return

    const loadPapers = async () => {
      try {
        setLoading(true)
        let papersList: InteractivePaper[] = []

        // Always load papers for the anonymous user if one exists
        if (anonymousUserId) {
          const anonymousPapersQuery = query(
            collection(firebaseDb, 'studentInteractivePapers'),
            where('anonymousUserId', '==', anonymousUserId),
            orderBy('createdAt', 'desc'),
          )

          const anonymousPapersSnapshot = await getDocs(anonymousPapersQuery)

          if (!anonymousPapersSnapshot.empty) {
            const anonymousPapers = anonymousPapersSnapshot.docs.map(
              (doc) =>
                ({
                  id: doc.id,
                  ...doc.data(),
                } as InteractivePaper),
            )

            papersList = [...papersList, ...anonymousPapers]
          }
        }

        // If user is authenticated, also load their papers
        if (user) {
          const userPapersQuery = query(
            collection(firebaseDb, 'studentInteractivePapers'),
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc'),
          )

          const userPapersSnapshot = await getDocs(userPapersQuery)

          if (!userPapersSnapshot.empty) {
            const userPapers = userPapersSnapshot.docs.map(
              (doc) =>
                ({
                  id: doc.id,
                  ...doc.data(),
                } as InteractivePaper),
            )

            // Filter out duplicates (papers that might exist in both queries)
            const existingIds = new Set(papersList.map((paper) => paper.id))
            const uniqueUserPapers = userPapers.filter(
              (paper) => !existingIds.has(paper.id),
            )

            papersList = [...papersList, ...uniqueUserPapers]
          }
        }

        // Sort papers by date (most recent first)
        papersList.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds)

        const userId = user?.uid || anonymousUserId

        let papersWithProgress = await Promise.all(
          papersList.map(async (paper) => {
            try {
              // Handle both topicId (single) and topicIds (array) formats
              const topicId =
                paper.topicId ||
                ((paper as any).topicIds && (paper as any).topicIds[0])

              if (!topicId) {
                console.warn(`Paper ${paper.id} has no topicId or topicIds`)
                return paper
              }

              const lessonsRef = collection(firebaseDb, 'lessons')
              const lessonsQuery = query(
                lessonsRef,
                where('topicId', '==', topicId),
              )

              const lessonsSnapshot = await getDocs(lessonsQuery)

              if (lessonsSnapshot.empty) {
                console.log(
                  `No lessons found for paper ${paper.id} with topicId ${topicId}`,
                )
                return paper
              }

              const lessonIds = lessonsSnapshot.docs.map((doc) => doc.id)

              let totalEarnedMarks = 0

              for (const lessonId of lessonIds) {
                const historyDocId = `${userId}-${lessonId}`
                const historyRef = doc(
                  firebaseDb,
                  'examPaperHistories',
                  historyDocId,
                )

                try {
                  const historySnap = await getDoc(historyRef)

                  if (historySnap.exists()) {
                    const historyData = historySnap.data()

                    // Check if earnedMarks exists and is a number
                    if (
                      historyData.earnedMarks !== undefined &&
                      typeof historyData.earnedMarks === 'number'
                    ) {
                      // Add to total earned marks
                      totalEarnedMarks += Math.round(historyData.earnedMarks)
                    }
                  }
                } catch (historyError) {
                  console.error(
                    `Error fetching history for lesson ${lessonId}:`,
                    historyError,
                  )
                }
              }

              console.log(
                `Paper ${paper.id} has ${totalEarnedMarks} earned marks`,
              )
              return { ...paper, earnedMarks: totalEarnedMarks }
            } catch (error) {
              console.error(`Error getting progress for ${paper.id}:`, error)
              return paper
            }
          }),
        )

        // apply paper updates for auth flow
        if (user && !!papersWithProgress.length) {
          // add fields to update
          const updateObj: Partial<InteractivePaper> = {
            userId: user.uid,
          }

          // check if user has paper to use while has no paid plan
          const hasPaperToUseWhileNoPaidPlan = papersWithProgress.find(
            (p) => p.isAccessibleWhileNoPaidPlan,
          )

          // get most recent paper, list is already sorted by date
          let mostRecentPaper = papersWithProgress[0]

          // update papers with userId and isAccessibleWhileNoPaidPlan (if needed)
          for (const paper of papersWithProgress) {
            const paperDocRef = doc(
              firebaseDb,
              'studentInteractivePapers',
              paper.id,
            )

            // get lessons of target paper to be updated with userId field
            const lessonsRef = collection(firebaseDb, 'lessons')
            const topicId =
              paper.topicId ||
              ((paper as any).topicIds && (paper as any).topicIds[0])

            if (!topicId) {
              console.warn(
                `Paper ${paper.id} has no topicId or topicIds, skipping lesson updates`,
              )
              continue
            }

            const lessonsQuery = query(
              lessonsRef,
              where('topicId', '==', topicId),
            )

            // get lessons snapshot
            const snapshot = await getDocs(lessonsQuery)

            // update lessons if not empty
            if (!snapshot.empty) {
              const batch = writeBatch(firebaseDb)

              let lessonsToUpdateCount = 0

              snapshot.forEach((doc) => {
                const data = doc.data()

                // update only lessons without userId and increase counter
                if (!data.userId) {
                  batch.update(doc.ref, { userId: user.uid })
                  lessonsToUpdateCount += 1
                }
              })

              if (!!lessonsToUpdateCount) {
                console.debug(
                  `Found ${lessonsToUpdateCount} lessons to be updated with userId field, paper id: ${paper.id}`,
                )
              }

              await batch.commit()

              if (!!lessonsToUpdateCount) {
                console.debug(
                  `Lessons for paper ${paper.id} updated with userId successfully`,
                  { paper },
                )
              }
            }

            // apply interaction restriction while no paid plan
            if (
              !hasPaperToUseWhileNoPaidPlan &&
              paper.id === mostRecentPaper.id
            ) {
              const mostRecentPaperUpdateObj: Partial<InteractivePaper> = {
                ...updateObj,
                isAccessibleWhileNoPaidPlan: true,
              }

              await updateDoc(paperDocRef, {
                ...mostRecentPaperUpdateObj,
                updatedAt: serverTimestamp(),
              })

              console.debug(`Paper ${paper.id} updated with userId`, { paper })

              mostRecentPaper = {
                ...mostRecentPaper,
                ...mostRecentPaperUpdateObj,
              }

              console.debug(
                `Paper ${paper.id} marked as "isAccessibleWhileNoPaidPlan" and userId field added`,
                { paper: mostRecentPaper },
              )

              continue
            }

            // update paper with userId if field is missing
            if (!paper.userId) {
              await updateDoc(paperDocRef, {
                ...updateObj,
                updatedAt: serverTimestamp(),
              })

              console.debug(`Paper ${paper.id} updated with userId field`, {
                paper: { ...paper, ...updateObj },
              })
            }
          }

          // update papers list
          papersWithProgress = papersWithProgress.map((paper) => {
            if (paper.id === mostRecentPaper.id) {
              return mostRecentPaper
            }

            if (!paper.userId) {
              return { ...paper, userId: user.uid }
            }

            return paper
          })
        }

        setPapers(papersWithProgress)

        // Convert to unified format for ExamSphere cards
        const unified = papersWithProgress.map(convertInteractivePaperToUnified)
        setUnifiedPapers(unified)
      } catch (error) {
        console.error('Error loading papers:', error)
        toast.error('Failed to load your interactive papers')
      } finally {
        setLoading(false)
      }
    }

    loadPapers()
  }, [anonymousUserId, user])

  const navigateToUpload = () => {
    router.push(
      `/interactive-papers/create?entry=interactive_papers_list&goBackUrl=${encodeURIComponent(
        '/interactive-papers',
      )}`,
    )
  }

  const handleDeletePaper = useCallback(
    async (paper: IUnifiedExamSpherePaper) => {
      try {
        setIsDeleting(paper.id)

        trackEventMixpanel(INTERACTIVE_PAPER_DELETED, {
          paperId: paper.id,
          paperTitle: paper.title || '',
        })

        // Delete the document from Firestore
        const docRef = doc(firebaseDb, 'studentInteractivePapers', paper.id)
        await deleteDoc(docRef)

        // Try to delete message subcollections if they exist
        try {
          if (user) {
            const userMessagesRef = doc(
              firebaseDb,
              'studentInteractivePapers',
              paper.id,
              'messages',
              user.uid,
            )
            await deleteDoc(userMessagesRef)
          }

          if (anonymousUserId) {
            const anonMessagesRef = doc(
              firebaseDb,
              'studentInteractivePapers',
              paper.id,
              'messages',
              anonymousUserId,
            )
            await deleteDoc(anonMessagesRef)
          }
        } catch (error) {
          console.error('Error deleting message subcollections:', error)
          // Continue even if subcollection deletion fails
        }

        // Update local state to remove the deleted paper
        const updatedPapers = papers.filter((p) => p.id !== paper.id)
        setPapers(updatedPapers)
        setUnifiedPapers(updatedPapers.map(convertInteractivePaperToUnified))

        toast.success('Paper deleted successfully')
      } catch (error) {
        console.error('Error deleting paper:', error)
        toast.error('Failed to delete paper')
      } finally {
        setIsDeleting(null)
      }
    },
    [papers, user, anonymousUserId],
  )

  const hasPapers = papers.length > 0

  return (
    <div
      className="w-full max-w-screen-2xl mx-auto p-4 lg:py-8 lg:px-4 flex flex-col gap-6 lg:gap-8"
      style={{
        minHeight: isInIframe
          ? '100vh'
          : !!user
          ? 'calc(100vh - var(--student-header-height))'
          : 'calc(100vh - var(--header-height-in-px))',
      }}
    >
      {/* Breadcrumbs */}
      <CommonBreadCrumbs
        breadcrumbs={
          // for embedded flow we do not want to give ability to change page to user
          isInIframe
            ? [
                {
                  title: '',
                },
              ]
            : [
                {
                  title: '',
                  href: !!user ? '/student' : '/',
                },
                { title: 'Interactive Papers' },
              ]
        }
        additionalHomeElement={isInIframe ? <>Interactive Papers</> : undefined}
      />

      {/* Header Section with Animated Background */}
      <motion.div
        className="relative bg-white rounded-xl shadow-lg overflow-hidden"
        variants={headerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Animated Background */}
        <ExamSphereAnimatedBackground className="opacity-40" />

        {/* Header Content */}
        <div className="relative z-10 p-6 lg:p-8">
          <div className="mb-4">
            <h1 className="text-2xl lg:text-3xl font-bold text-v8-ink mb-2">
              Interactive Exam Papers
            </h1>
            <p className="text-v8-mut">
              Practice with real exam papers and get AI feedback on your answers
            </p>
          </div>

          {/* Action Button */}
          <button
            onClick={navigateToUpload}
            className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-full text-white bg-v8-gradient hover:opacity-90 transition-opacity shadow-md"
          >
            <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
            Upload New Paper
          </button>
        </div>
      </motion.div>

      {/* Papers Section */}
      {loading ? (
        <div className="space-y-8">
          <ExamSpherePaperRow title="Loading..." papers={[]} isLoading={true} />
        </div>
      ) : !hasPapers ? (
        /* Empty State */
        <motion.div
          className="bg-white rounded-v8 shadow-v8-card border border-v8-line p-10 lg:p-16"
          variants={emptyStateVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="text-center max-w-2xl mx-auto">
            <div className="mb-6">
              <Cube3dSphereIcon
                width="64"
                height="64"
                className="mx-auto text-v8-blue/40 stroke-[1.5]"
              />
            </div>
            <h3 className="text-2xl font-bold text-v8-ink mb-3">
              No Papers Yet
            </h3>
            <p className="text-v8-mut mb-8 text-lg">
              Start your exam preparation by uploading a past paper to practice
              with AI-powered feedback.
            </p>
            <button
              onClick={navigateToUpload}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-white bg-v8-gradient hover:opacity-90 transition-opacity shadow-md"
            >
              <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
              Upload Your First Paper
            </button>
          </div>
        </motion.div>
      ) : (
        /* Papers Row */
        <ExamSpherePaperRow
          title="Your Uploaded Papers"
          papers={unifiedPapers}
          isLoading={false}
          onDeletePaper={handleDeletePaper}
          isDeletingId={isDeleting}
        />
      )}
    </div>
  )
}

export default InteractivePapersList
